/*
Esta página muestra el detalle de una Sucursal.
Autor: Carolina Rojas
Fecha: 
----------------
Modificaciones
BLC 2025-02-13 : Agregamos comuna
*/

import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { BranchDto } from '../interfaces/Branch' // Adjust the import path as needed
import useBranchesFetcher from '../hooks/useBranchesFetcher'
import { BranchState } from '../enums/DocStates'
import useBranchActivate from '../hooks/useBranchActivate'
import { formatDateToYYYY_MM_DD, insertAfterFirstToken } from '../utils/utils'

import { ButtonAction, ButtonCheckConfirmaEx } from './Buttons'
import { DisplayValueFormat } from './DisplayValue'
import BranchStatusHeader from './BranchStatusHeader'
import { Info } from './MessageInfo'

import { AppPath } from '@/enums/AppPath'

const BranchDetail: React.FC = () => {
  const { errorBranches, loading, fetchBranchDetail } = useBranchesFetcher()
  const [dataBranch, setDataBranch] = useState<BranchDto | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const [infoMessage, setInfoMessage] = useState('')
  const { branchActivate, errorBranchActivate } = useBranchActivate()
  const [check, setCheck] = useState<boolean>(false)
  const [inactivo, setInactivo] = useState<boolean>(!!dataBranch?.disabledAt)

  // Extracting id parameter from the route
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  useEffect(() => {
    if (!id) {
      setLocalError('ID is not defined.')

      return // Exit early if `id` is undefined
    }

    const fetchDataAsync = async (id: string) => {
      const branch = await fetchBranchDetail(id)

      if (branch) {
        setDataBranch(branch)
        setInactivo(!!dataBranch?.disabledAt)
      }
    }

    fetchDataAsync(id)
  }, [id])

  //Inactivo se actualiza según el cambio en dataBranch
  useEffect(() => {
    if (dataBranch) {
      setInactivo(!!dataBranch.disabledAt)
    }
  }, [dataBranch])

  //Actualizamos la fecha dependiendo si la sucursal está inactiva, depende de dataBranc e inactivo
  useEffect(() => {
    if (dataBranch) {
      const date = dataBranch.disabledAt ? formatDateToYYYY_MM_DD(new Date(dataBranch.disabledAt)) : ''
      setBranchInactive({
        state: inactivo ? BranchState.Inactivo : BranchState.Activo,
        date: date,
      })
    }
  }, [dataBranch, inactivo])

  const [branchInactive, setBranchInactive] = useState({
    state: BranchState.Inactivo,
    date: dataBranch?.disabledAt ?? '',
  })

  const onInfoTimeout = (): void => {
    setInfoMessage('')
  }

  //Activar o desactivar la sucursal
  const onActivateClick = async () => {
    if (!dataBranch) return
    const id = dataBranch._id
    const msg = await branchActivate(id, inactivo)

    const infoMsg = inactivo ? 'Sucursal activada.' : 'Sucursal desactivada.'

    if (msg) {
      setInfoMessage(infoMsg)
      //volvemos a la ventana de detalle.
      const url = `/${AppPath.Branches}/view/${id}`
      const date = inactivo ? '' : formatDateToYYYY_MM_DD(new Date())
      setBranchInactive({ ...branchInactive, date: date })

      //Se resetean los valores
      setInactivo(!inactivo)
      setBranchInactive((prev) => ({
        ...prev,
        date: !inactivo ? formatDateToYYYY_MM_DD(new Date()) : '',
      }))
      setCheck(false)
      navigate(url)

      fetchBranchDetail(id).then(setDataBranch)
    }
  }

  //Modificar los datos de la sucursal
  const handleEditar = () => {
    navigate(`/${AppPath.Branches}/edit/${id}`)
  }

  //Leyendas
  const title = inactivo ? 'Activar sucursal' : 'Desactivar sucursal'
  const subTitle = inactivo
    ? 'Al restaurar la sucursal esta podrá ser asignada a los vehículos.'
    : 'Al desactivar la sucursal esta no podrá ser asignada a un vehículo.'
  const checkLabel = inactivo ? 'Estoy seguro de restaurar esta sucursal.' : 'Estoy seguro de desactivar esta sucursal.'
  const buttonLabel = inactivo ? 'Restaurar' : 'Desactivar'

  const c1 = dataBranch?.address ?? ''
  const c2 = (dataBranch?.streetNumber ?? '').toString()
  const c3 = dataBranch?.addressComponents ?? ''
  const fullAddress = insertAfterFirstToken(c1, c2, c3)

  if (loading) return <div>Loading...</div>

  if (errorBranches) return <div>Error: {errorBranches.message}</div>

  if (localError) return <div>Error: {localError}</div>

  return (
    <div className=" mb-4">
      {inactivo && <BranchStatusHeader name="sucursal" state={branchInactive.state} date={branchInactive.date} />}

      <div className="text-2xl font-bold mb-2 mt-4"> Detalles de la sucursal</div>
      <div className="grid grid-cols-[auto,1fr] gap-x-4">
        <DisplayValueFormat label="Nombre" value={dataBranch?.name} />
        {/**   <DisplayValueFormat label="Comuna" value={dataBranch?.municipality_format} />
         */}
        <DisplayValueFormat label="Dirección" value={fullAddress} />
        <DisplayValueFormat label="Email" value={dataBranch?.email} />
        <DisplayValueFormat label="Teléfono" value={dataBranch?.phone} />
        <DisplayValueFormat label="WhatsApp" value={dataBranch?.whatsapp} />
      </div>
      <div className="flex justify-center mt-4">
        <ButtonAction label="Modificar..." handleButtonClick={handleEditar} />
      </div>

      <ButtonCheckConfirmaEx
        title={title}
        subTitle={subTitle}
        checkLabel={checkLabel}
        buttonLabel={buttonLabel}
        handleCrearClick={onActivateClick}
        check={check}
        setCheck={setCheck}
      />

      <Info message={infoMessage} trigger={Date.now()} onTimeout={onInfoTimeout} noCenter={true} />

      <div>{errorBranchActivate?.message}</div>
    </div>
  )
}

export default BranchDetail
